import { useEffect, useState } from "react";
import { Copy, Share2, Gift, Users, Wallet, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function CustomerReferrals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/customer/referrals").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-center py-12 text-[#6B5F58]">Loading…</div>;

  const link = `https://passiveiearn.com/register?ref=${data.referral_code}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };
  const shareWA = () => {
    const e = String.fromCodePoint;
    const sheep = e(0x1F411), point = e(0x1F449);
    const msg =
      `${sheep} Hey! Join me on PassiveIearn — invest in livestock and earn passive income.\n\n` +
      `Use my code *${data.referral_code}* and we both get ₹500 credit on your first lot.\n\n` +
      `Sign up here ${point} ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-w-0 max-w-full">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-2xl md:rounded-3xl mb-5 md:mb-6 p-5 md:p-10 text-white"
        style={{ background: "linear-gradient(135deg, #B8862A 0%, #D71921 60%, #7A1010 100%)" }}>
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F2C97A]/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-[#3F6B3F]/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-[10px] md:text-xs font-bold uppercase tracking-widest mb-3">
            <Gift className="w-3.5 h-3.5 text-[#F2C97A]" /> Refer & Earn
          </div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-5xl leading-tight tracking-tight break-words">Share PassiveIearn,<br />both get <span className="text-[#F2C97A]">₹500</span>.</h1>
          <p className="text-sm md:text-base text-white/85 mt-2 max-w-xl">When a friend signs up using your code and registers, they get ₹500 credit and so do you.</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-5 md:mb-6">
        <Stat icon={Wallet} label="Your credit" value={`₹${(data.referral_credit || 0).toLocaleString()}`} gradient="linear-gradient(135deg, #3F6B3F 0%, #2A4A2A 100%)" testid="ref-credit" />
        <Stat icon={Users} label="Referrals" value={data.total_referrals} gradient="linear-gradient(135deg, #B8862A 0%, #8E6517 100%)" testid="ref-count" />
        <Stat icon={Gift} label="Earned so far" value={`₹${(data.total_referrals * 500).toLocaleString()}`} gradient="linear-gradient(135deg, #D71921 0%, #B0151E 100%)" testid="ref-earned" />
      </div>

      {/* Code & link card */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-[#E8E0D2] p-4 md:p-6 overflow-hidden">
          <div className="text-[11px] md:text-xs uppercase tracking-widest text-[#6B5F58] font-bold">Your code</div>
          <div className="mt-3 flex items-center gap-2">
            <div data-testid="ref-code" className="flex-1 min-w-0 rounded-xl border-2 border-dashed border-[#D71921] px-3 md:px-4 py-3 md:py-3.5 font-mono text-lg sm:text-xl md:text-2xl font-bold tracking-[0.2em] md:tracking-widest text-[#D71921] text-center bg-[#FCEDED] truncate">
              {data.referral_code}
            </div>
            <button onClick={() => copy(data.referral_code, "Code")} data-testid="ref-copy-code"
              className="flex-shrink-0 px-3 md:px-4 py-3 md:py-3.5 rounded-xl border border-[#E8E0D2] hover:border-[#D71921] hover:text-[#D71921] transition-colors">
              <Copy className="w-4 h-4" />
            </button>
          </div>

          <div className="text-[11px] md:text-xs uppercase tracking-widest text-[#6B5F58] font-bold mt-5">Your link</div>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 min-w-0 rounded-xl border border-[#E8E0D2] px-3 py-3 font-mono text-[11px] md:text-xs text-[#1F1816] truncate bg-[#F8F4E9]">
              {link}
            </div>
            <button onClick={() => copy(link, "Link")} data-testid="ref-copy-link"
              className="flex-shrink-0 px-3 py-3 rounded-xl border border-[#E8E0D2] hover:border-[#D71921] hover:text-[#D71921] transition-colors">
              <Copy className="w-4 h-4" />
            </button>
          </div>

          <button onClick={shareWA} data-testid="ref-share-wa"
            className="mt-5 w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-semibold shadow-lg shadow-[#3F6B3F]/25 hover:-translate-y-0.5 transition-all"
            style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}>
            <Share2 className="w-4 h-4" /> Share on WhatsApp
          </button>
        </div>

        <div className="rounded-2xl p-4 md:p-6 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1F1816 0%, #7A1010 100%)" }}>
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#F2C97A]/20 blur-3xl pointer-events-none" />
          <div className="relative">
            <Sparkles className="w-6 h-6 text-[#F2C97A]" />
            <div className="font-display text-lg md:text-xl mt-3">How it works</div>
            <ul className="mt-4 space-y-3 text-sm text-white/85">
              <Step num="1" text="Share your code or link with friends" />
              <Step num="2" text="They register on PassiveIearn using your code" />
              <Step num="3" text="Both of you get ₹500 platform credit" />
              <Step num="4" text="Credit auto-applies to your next lot purchase" />
            </ul>
          </div>
        </div>
      </div>

      {/* List of referred users */}
      <div className="mt-6">
        <h2 className="font-display text-xl md:text-2xl mb-3">Your referrals</h2>
        {data.referrals.length === 0 ? (
          <div className="rounded-2xl bg-white border border-[#E8E0D2] p-8 md:p-10 text-center">
            <Users className="w-10 h-10 text-[#6B5F58] mx-auto mb-3" />
            <div className="font-display text-lg">No referrals yet</div>
            <p className="text-sm text-[#6B5F58] mt-1">Share your code to get started.</p>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-[#E8E0D2] overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <thead className="bg-[#F8F4E9] text-xs uppercase tracking-wider text-[#6B5F58]">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">KYC</th>
                  <th className="text-left px-4 py-3">Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.map((r, i) => (
                  <tr key={i} className="border-t border-[#E8E0D2]">
                    <td className="px-4 py-3 font-medium">{r.full_name}</td>
                    <td className="px-4 py-3 text-[#6B5F58]">{r.phone}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        r.kyc_status === "approved" ? "bg-[#E6F0E5] text-[#3F6B3F]"
                        : r.kyc_status === "pending" ? "bg-amber-100 text-amber-800"
                        : "bg-stone-100 text-stone-700"}`}>{r.kyc_status || "—"}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B5F58]">{(r.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, gradient, testid }) {
  return (
    <div className="relative overflow-hidden rounded-xl md:rounded-2xl p-3 md:p-5 text-white" style={{ background: gradient }} data-testid={testid}>
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10 blur-xl pointer-events-none" />
      <Icon className="w-4 h-4 md:w-5 md:h-5 relative" />
      <div className="text-[9px] md:text-[10px] uppercase tracking-widest opacity-80 mt-1.5 md:mt-2 relative truncate">{label}</div>
      <div className="font-display text-base md:text-2xl mt-0.5 md:mt-1 relative break-all leading-tight">{value}</div>
    </div>
  );
}

function Step({ num, text }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-[#F2C97A]/20 border border-[#F2C97A]/40 grid place-items-center text-[#F2C97A] text-xs font-bold flex-shrink-0">{num}</div>
      <span>{text}</span>
    </li>
  );
}
